import React, { memo, useEffect } from 'react';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';

import {
  handleGetBuyerNameSuggestion,
  handleGetBuyerProjectNameSuggestion,
  handleUpdateForm,
  handleUpdateBuyerForm,
} from 'src/actions/emailTemplatesSendGrid';

import FormGroup from '../helpers/FormGroup';
import BaseInput from '../helpers/Input';
import PrettyError from '../decorators/PrettyError';
import DisplayError from '../decorators/DisplayError';

import { AutoComplete } from './AutoComplete';
import { fetchEventActivitiesOnlyValues } from '../../actions/eventActivities';

import style from './style.module.css';

const Input = PrettyError(DisplayError(BaseInput));

const EmailTemplateSendGridFormComponent = ({
  activities,
  handleGetBuyerNameSuggestion,
  handleUpdateBuyerForm,
  emailTemplate,
  handleGetBuyerProjectNameSuggestion,
  handleUpdateForm,
  fetchEventActivitiesOnlyValues,
  formName,
}) => {
  useEffect(() => {
    fetchEventActivitiesOnlyValues();
  }, []);

  const handleSubFormChange = data => {
    handleUpdateBuyerForm({ ...data, name: formName });
    if (data.formName === 'buyer' && !data.formData.selected) {
      handleUpdateBuyerForm({
        formName: 'project',
        formData: { text: '', selected: null },
        name: formName,
      });
    }
  };

  const handleChange = data => {
    handleUpdateForm({
      name: formName,
      formName: data.formName,
      formData: data.formData,
    });
  };

  const handleGetNextBuyerSuggestionForBuyer = ({ value }) => {
    const info = emailTemplate.getIn(['buyer', 'buyer', 'pagination']);
    const currentPage = info.get('currentPage', null);

    if (currentPage !== null && currentPage < info.get('totalPages')) {
      handleGetBuyerNameSuggestion(value, formName, currentPage + 1);
    }
  };

  const handleGetBuyerSuggestionForBuyer = ({ value }) => {
    handleGetBuyerNameSuggestion(value, formName);
  };

  const handleGetNextProjectSuggestionForBuyer = ({ value }) => {
    const buyerSelected = emailTemplate.getIn(['buyer', 'buyer', 'selected']);
    const info = emailTemplate.getIn(['buyer', 'project', 'pagination']);
    const currentPage = info.get('currentPage', null);

    if (buyerSelected && currentPage !== null && currentPage < info.get('totalPages')) {
      handleGetBuyerProjectNameSuggestion(buyerSelected.get('id'), value, formName, currentPage + 1);
    }
  };

  const handleGetProjectSuggestionForBuyer = ({ value }) => {
    const buyerSelected = emailTemplate.getIn(['buyer', 'buyer', 'selected']);

    if (buyerSelected) {
      handleGetBuyerProjectNameSuggestion(buyerSelected.get('id'), value, formName);
    }
  };

  const handleGetSuggestionForActivity = ({ value }) => {
    const suggestions = activities.map(emailActivity => ({
      name: emailActivity,
      value: emailActivity,
    }));

    const filteredSuggestions = suggestions.filter(
      emailActivity => emailActivity.value.toLowerCase().indexOf(value.toLowerCase()) > -1,
    );

    handleUpdateForm({
      name: formName,
      formName: 'activity',
      formData: {
        suggestions: filteredSuggestions,
      },
    });
  };

  const handleInputChange = event => {
    handleUpdateForm({
      name: formName,
      formName: event.target.name,
      formData: {
        value: event.target.value,
      },
    });
  };

  const isFilter = formName === 'filter';
  const fieldClassName = isFilter ? style.filterField : 'col-md-3';

  const inputPropsBuyer = fromJS({
    formName: 'buyer',
    label: isFilter ? null : 'Buyer',
    placeholder: 'Buyer',
    renderField: 'text',
  });

  const inputPropsProject = fromJS({
    formName: 'project',
    label: isFilter ? null : 'Project',
    placeholder: 'Project',
    renderField(suggestion) {
      return `${suggestion.category} (${suggestion.harvcoLead})`;
    },
    disabled: !emailTemplate.getIn(['buyer', 'buyer', 'selected']),
  });

  const inputPropsActivity = fromJS({
    formName: 'activity',
    label: isFilter ? null : 'Activity',
    placeholder: 'Activity',
    renderField: 'value',
  });

  return (
    <>
      {!isFilter && (
        <div className="row">
          <div className="col-md-6">
            <FormGroup>
              <label>Subject</label>
              <Input
                className="input-md"
                name="subject"
                onChange={handleInputChange}
                placeholder="Subject"
                value={emailTemplate.getIn(['subject', 'value'])}
              />
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <label>From Name</label>
              <Input
                className="input-md"
                name="fromName"
                onChange={handleInputChange}
                placeholder="From Name"
                value={emailTemplate.getIn(['fromName', 'value'])}
              />
            </FormGroup>
          </div>
          <div className="col-md-3">
            <FormGroup>
              <label>From Email</label>
              <Input
                className="input-md"
                name="fromEmail"
                onChange={handleInputChange}
                placeholder="From Email"
                value={emailTemplate.getIn(['fromEmail', 'value'])}
              />
            </FormGroup>
          </div>
        </div>
      )}
      <div className={isFilter ? style.filterFields : 'row'}>
        <div className={fieldClassName}>
          <AutoComplete
            info={emailTemplate.get('activity')}
            inputProps={inputPropsActivity}
            onChange={handleChange}
            onGetSuggestion={handleGetSuggestionForActivity}
          />
        </div>
        <div className={fieldClassName}>
          <AutoComplete
            info={emailTemplate.getIn(['buyer', 'buyer'])}
            inputProps={inputPropsBuyer}
            onChange={handleSubFormChange}
            onGetNextSuggestion={handleGetNextBuyerSuggestionForBuyer}
            onGetSuggestion={handleGetBuyerSuggestionForBuyer}
          />
        </div>
        <div className={fieldClassName}>
          <AutoComplete
            info={emailTemplate.getIn(['buyer', 'project'])}
            inputProps={inputPropsProject}
            onChange={handleSubFormChange}
            onGetNextSuggestion={handleGetNextProjectSuggestionForBuyer}
            onGetSuggestion={handleGetProjectSuggestionForBuyer}
          />
        </div>
        <div className={fieldClassName}>
          <FormGroup>
            {!isFilter && <label>Template Name</label>}
            <Input
              className="input-md"
              name="templateName"
              onChange={handleInputChange}
              placeholder="Template Name"
              value={emailTemplate.getIn(['templateName', 'value'])}
            />
          </FormGroup>
        </div>
      </div>
    </>
  );
};

/** Email template send grid form component. */
export const EmailTemplateSendGridForm = memo(
  connect(null, {
    handleGetBuyerNameSuggestion,
    handleUpdateBuyerForm,
    handleGetBuyerProjectNameSuggestion,
    handleUpdateForm,
    fetchEventActivitiesOnlyValues,
  })(EmailTemplateSendGridFormComponent),
);
